import { DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, filter, lastValueFrom, map } from 'rxjs';
import { Assignment, CreateAssignmentBody } from '../../../api/app-types/assignment';
import { Client } from '../../../api/app-types/client.app';
import { DB_PHASES } from '../../../api/app-types/phase.app';
import { User } from '../../../api/app-types/user.app';
import { ASSIGNMENT_API } from '../../../api/services/assignment.service';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { ActionMenuItem, LvrActionMenu } from '../../../core/components/action-menu/action-menu';
import { LvrAssignmentAccordion } from '../../../core/components/assignment-accordion/assignment-accordion';
import { AssignmentDraftCreateDialog } from '../../../core/components/assignment-draft-create-dialog/assignment-draft-create-dialog';
import { AssignmentDraftDeleteDialog } from '../../../core/components/assignment-draft-delete-dialog/assignment-draft-delete-dialog';
import { GenericDialog } from '../../../core/components/generic-dialog/generic-dialog';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrSimplePanel } from '../../../core/components/simple-panel/simple-panel';
import { AccessService } from '../../../core/services/access.service';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { TENANT_FEATURES } from '../../../core/services/tenant-feature.service';
import { STATE } from '../../../core/services/state.service';
import { resolveAssignmentState } from '../../../shared/helpers/assignment.func';
import { formatDateToISOString } from '../../../shared/helpers/date.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createInfoNotification } from '../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { ClientResolved } from '../../../shared/routing/resolvers/client.resolver';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-contact-view',
	imports: [
		DatePipe,
		TranslocoPipe,
		LvrActionMenu,
		LvrPaddingDirective,
		LvrSimplePanel,
		LvrAssignmentAccordion,
		MatDialogModule,
		LvrButton,
	],
	templateUrl: './contact-view.html',
	styleUrl: './contact-view.scss',
})
export class ContactView implements OnInit {
	// These 2 signals below are null effect. Because of time time pressure, we did not refactor the code to remove them. They are used in the template and are required for the template to compile.
	public readonly assignmentContactsLoading = signal<Record<number, boolean>>({});
	public readonly assignmentContactsError = signal<Record<number, boolean>>({});

	private readonly destroyRef = inject(DestroyRef);
	private readonly rm = inject(RECORD_MANAGER);
	private readonly api = inject(ASSIGNMENT_API);
	private readonly state = inject(STATE);
	private readonly access = inject(AccessService);
	private readonly representationMode = inject(REPRESENTATION_MODE);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	private readonly router = inject(Router);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly data = toSignal(this.activatedRoute.data);
	private readonly dialog = inject(MatDialog);
	public readonly assignments = computed(() => this.state.currentAssignments().map((a) => this.enrichAssignment(a)));
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	public readonly assignmentsLoading = signal(true);
	private readonly requestedClientId = signal<number | null>(null);
	private clientLoadVersion = 0;
	public readonly canCreateContacts = hasPermission(this.session.permissions, [
		'Contacts.Create',
		'Contacts.Create.PhaseOneOnly',
	]);
	public readonly canTransitionPhases = hasPermission(this.session.permissions, 'Phases.Transition');
	public readonly canDeleteAssignments = hasPermission(this.session.permissions, 'Assignments.Delete');
	public readonly activeAssignment = computed(() =>
		this.assignments().find((a) => !this.isFdsAssignment(a) && a.phaseId !== DB_PHASES.P4 && !a.isAdditionalAssignment),
	);
	public readonly activeAssignmentContactsCount = computed(() => this.activeAssignment()?.contactsCount ?? 0);
	public readonly assignmentsWithoutP0andContacts = computed(() => {
		if (this.isClientChanging()) {
			return [];
		}

		return this.assignments().filter(
			(assignment) => assignment.phaseId !== DB_PHASES.P0 || (assignment.contactsCount ?? 0) !== 0,
		);
	});
	private readonly clientResolved = computed<ClientResolved | undefined>(() => {
		return this.data()?.['client'] as ClientResolved | undefined;
	});
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly totalAssignments = computed(() => this.assignments()?.length ?? 0);
	public readonly user = computed(() => this.data()?.['user'] as User);
	public readonly shouldEnableCreateAssignment = computed(() => {
		const active = this.activeAssignment();
		if (!active) return false;

		return active.phaseId === DB_PHASES.P0 && (active.contactsCount ?? 0) > 0;
	});
	public readonly assignmentPhase = computed(() => resolveAssignmentState(this.activeAssignment()));
	public readonly canCreateAdditionalAssignment = computed(() => {
		if (this.activeAssignment()?.phaseId === DB_PHASES.P3) return true;
		return false;
	});
	public readonly isMainOwnerForActiveAssignment = computed(() => {
		const user = this.user();
		const assignment = this.activeAssignment();
		if (!user || !assignment) return false;
		return this.access.isMainOwner(assignment, user);
	});
	public readonly canCreateAssignment = computed(() => {
		// if phase is "NO_ASSIGNMENT", we can't create a new one
		if (this.assignmentPhase() === 'NO_ASSIGNMENT') return false;

		// TODO: Add other conditions here later
		return true;
	});
	public readonly createContactLink = (assignment: Assignment) => {
		return `/desk/client/${this.client().id}/assignments/${assignment.id}/contacts/add`;
	};

	public readonly activeAssignmentLink = computed(() => {
		const assignment = this.activeAssignment();
		return assignment ? this.createContactLink(assignment) : '';
	});

	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;

	public ngOnInit(): void {
		this.activatedRoute.paramMap
			.pipe(
				map((params) => params.get('id')),
				filter((clientId): clientId is string => clientId !== null),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((clientId) => {
				this.requestedClientId.set(Number(clientId));
				void this.loadClientState(clientId);
			});
	}

	public readonly isClientChanging = computed(() => {
		const requestedClientId = this.requestedClientId();
		const loadedClientId = this.state.currentClient()?.id;

		return this.assignmentsLoading() || requestedClientId === null || loadedClientId !== requestedClientId;
	});

	private async loadClientState(clientId: string): Promise<void> {
		const loadVersion = ++this.clientLoadVersion;

		this.assignmentsLoading.set(true);

		try {
			await this.state.setCurrentClient(clientId);

			if (loadVersion !== this.clientLoadVersion) {
				return;
			}

			const client = this.state.currentClient();

			if (!client || client.id !== Number(clientId)) {
				return;
			}

			setTimeout(() => {
				if (this.state.currentClient()?.id !== client.id) {
					return;
				}

				this.rm.updateRecordTitleOfActiveRecord(client.firstName ? `${client.name}, ${client.firstName}` : client.name);
			}, 500);
		} finally {
			if (loadVersion === this.clientLoadVersion) {
				this.assignmentsLoading.set(false);
			}
		}
	}

	public openCreate() {
		if (!this.canCreateContacts()) return;
		this.router.navigate([this.activeAssignmentLink()]);
	}

	/**
	 * The list of menu items for the action menu.
	 */
	public readonly menuItems = computed<ActionMenuItem[]>(() => {
		const canCreate = this.shouldEnableCreateAssignment();
		const isMainOwner = this.isMainOwnerForActiveAssignment();
		const additionalAssignmentEnabled = this.tenantFeatures.additionalAssignmentEnabled();

		const tooltipCreate = canCreate
			? 'Auftrag anlegen'
			: this.assignmentPhase() === 'NO_ASSIGNMENT'
				? 'Kein Kontakt angelegt'
				: 'Bereits vorhanden';
		const tooltipAdditional = this.canCreateAdditionalAssignment()
			? 'Zusatzauftrag anlegen'
			: 'Zusatzaufträge in Phase P3 möglich';
		const tooltipAdditionalOwner = !isMainOwner ? 'Keine Berechtigung fuer Zusatzauftrag' : tooltipAdditional;
		return [
			{
				label: 'Auftrag',
				icon: 'add',
				tooltip: tooltipCreate,
				disabled: !canCreate,
				onClick: () => this.onAssignmentClick(),
			},
			// The additional assignment entry is tenant-controlled and is removed entirely when disabled.
			...(additionalAssignmentEnabled
				? [
						{
							label: 'Zusatzauftrag',
							icon: 'add',
							tooltip: tooltipAdditionalOwner,
							disabled: !this.canCreateAdditionalAssignment() || !isMainOwner,
							onClick: () => void this.openAdditionalAssignmentDialog(),
						},
					]
				: []),
			{
				label: 'FDS Auftrag',
				icon: 'add',
				disabled: !isMainOwner,
				routerLink: isMainOwner ? ['../', 'assignments', 'add-fds'] : '',
			},
		];
	});

	private async openAdditionalAssignmentDialog() {
		if (!this.canCreateAdditionalAssignment() || !this.isMainOwnerForActiveAssignment()) return;
		const assignment = this.activeAssignment();
		const remark = assignment?.phase?.remark ?? '';
		const assignmentLabel = remark ? `Betreuung ${remark}` : 'Betreuung | P3';
		const confirmed = await lastValueFrom(
			this.dialog
				.open(GenericDialog, {
					data: {
						titleKey: 'assignment.additional.dialog.title',
						confirmKey: 'assignment.additional.dialog.confirm',
						cancelKey: 'base.button.cancel',
						contextLabel: assignmentLabel,
						iconConfirm: 'add',
					},
				})
				.afterClosed(),
		);
		if (!confirmed) return;
		this.router.navigate(['../', 'assignments', 'add-additional'], {
			relativeTo: this.activatedRoute,
		});
	}

	public async openPhaseSwitchDialog(assignment: Assignment) {
		if (!this.canTransitionPhases()) return;
		if (assignment.isServiceOfficeStatement) {
			const targetPhaseId = DB_PHASES.FP4;
			if (assignment.phaseChangeId !== targetPhaseId) {
				await this.api.startPhaseTransition(this.client().id, assignment.id, targetPhaseId);
				await this.state.updateAssignments();
			}

			this.router.navigate([assignment.id, 'edit-fds'], {
				relativeTo: this.activatedRoute,
				queryParams: { redirectUrl: `desk/client/${this.client().id}/assignments/${assignment.id}/fds-move` },
			});
			return;
		}

		if (assignment.isAdditionalAssignment) {
			const targetPhaseId = DB_PHASES.P3;
			if (assignment.phaseChangeId !== targetPhaseId) {
				await this.api.startPhaseTransition(this.client().id, assignment.id, targetPhaseId);
				await this.state.updateAssignments();
			}
			this.router.navigate([assignment.id, 'edit-additional'], {
				relativeTo: this.activatedRoute,
				queryParams: { redirectUrl: `desk/client/${this.client().id}/assignments/${assignment.id}/additional-move` },
			});
			return;
		}

		const current = assignment.phase?.id;
		if (!current) return;

		let targetPhaseId: number | undefined;

		// If assignment currently in P2
		if (current === DB_PHASES.P2) {
			targetPhaseId = DB_PHASES.P3;
		} else if (current === DB_PHASES.P1) {
			// If assignment currently in P1
			targetPhaseId = assignment.draftPhaseId;
			if (!targetPhaseId) return;
		} else {
			return;
		}

		if (!assignment.phaseChangeId) {
			await this.api.startPhaseTransition(this.client().id, assignment.id, targetPhaseId);
			await this.state.updateAssignments();
		}

		// Draft flow should not force navigation to edit if all required data is already present.
		if (current === DB_PHASES.P1) {
			try {
				const latestAssignment = this.state.currentAssignments().find((a) => a.id === assignment.id);
				if (!latestAssignment) throw new Error('Assignment not found after transition start');

				await this.router.navigate([latestAssignment.id, 'move'], {
					relativeTo: this.activatedRoute,
				});
				return;
			} catch {
				// Fall back to edit flow below.
			}
		}

		this.router.navigate([assignment.id, 'edit'], {
			relativeTo: this.activatedRoute,
			queryParams: { redirectUrl: `desk/client/${this.client().id}/assignments/${assignment.id}/move` },
		});
	}

	public async moveToCancelAssignment(assignment: Assignment) {
		if (!this.canTransitionPhases()) return;
		const targetPhaseId = assignment.isServiceOfficeStatement ? DB_PHASES.FP5 : DB_PHASES.P4;
		await this.api.startPhaseTransition(this.client().id, assignment.id, targetPhaseId);
		await this.state.updateAssignments();

		const routeSegments = assignment.isServiceOfficeStatement
			? [assignment.id, 'edit-fds']
			: assignment.isAdditionalAssignment
				? [assignment.id, 'edit-additional']
				: [assignment.id, 'edit'];

		const queryParams = assignment.isServiceOfficeStatement
			? { redirectUrl: `desk/client/${this.client().id}/assignments/${assignment.id}/fds-move` }
			: assignment.isAdditionalAssignment
				? { redirectUrl: `desk/client/${this.client().id}/assignments/${assignment.id}/additional-move` }
				: { redirectUrl: `desk/client/${this.client().id}/assignments/${assignment.id}/move` };

		this.router.navigate(routeSegments, {
			relativeTo: this.activatedRoute,
			queryParams: queryParams,
		});
	}

	private isFdsAssignment(assignment: Assignment) {
		return assignment.isServiceOfficeStatement === true || assignment.phaseId >= DB_PHASES.FP3;
	}

	public async onAssignmentClick() {
		const assignment = this.activeAssignment();
		if (!assignment) return;

		const targetPhaseId = await lastValueFrom(
			this.dialog.open(AssignmentDraftCreateDialog, { width: '400px', data: { assignment } }).afterClosed(),
		);

		if (!targetPhaseId) return;

		try {
			// Move assignment from P0 to P1
			await this.moveAssignmentToP1(assignment);

			// Set DraftPhaseId
			await this.api.setDraftPhase(assignment.clientId, assignment.id, targetPhaseId);

			// Create
			this.router.navigate(['../', 'assignments', 'add'], {
				relativeTo: this.activatedRoute,
			});
		} catch (e) {
			console.error(e);
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.assignment_create_error')),
			);
		}
	}

	public async onDeleteDraft(a: Assignment) {
		if (!this.canDeleteAssignments()) {
			this.ntfy.addNotification(
				createInfoNotification(this.transloco.translate('base.notification_message.generic_denied_permission')),
			);
			return;
		}
		const result = await lastValueFrom(
			this.dialog
				.open(AssignmentDraftDeleteDialog, {
					width: '400px',
					data: {
						assignment: a,
					},
				})
				.afterClosed(),
		);

		if (!result) return;

		// Reload assignments
		await this.state.updateAssignments();
	}

	public async onAdditionalDeleted(_assignment: Assignment) {
		try {
			// Reload assignments
			await this.state.updateAssignments();

			const assignments = this.state.currentAssignments();
			const hasAdditionalAssignments = assignments.some(
				(assignment) => assignment.isAdditionalAssignment || (assignment.additionalAssignments?.length ?? 0) > 0,
			);

			// If the user no longer has any additional assignments, navigate to the dashboard
			if (!hasAdditionalAssignments) {
				await this.router.navigate(['/']);
			}
		} catch (e) {
			console.error(e);
		}
	}

	/**
	 * Moves the assignment phase from P0 to P1.
	 * */
	private async moveAssignmentToP1(assignment: Assignment): Promise<void> {
		if (assignment.phaseId !== DB_PHASES.P0) return;

		const P1_ID = DB_PHASES.P1;

		const payload: Partial<CreateAssignmentBody> = {
			phaseId: P1_ID,
			phase: { id: P1_ID, remark: 'P1' },
			phaseStart: new Date().toISOString(),
			contactStart: formatDateToISOString(assignment.contactStart),
		};

		await this.api.create(assignment.clientId, assignment.id, payload as CreateAssignmentBody);
	}

	private enrichAssignment(assignment: Assignment): Assignment {
		const contacts = this.state.currentContacts()[assignment.id] ?? [];

		const additionalAssignments = (assignment.additionalAssignments ?? []).map((additional) =>
			this.enrichAssignment(additional),
		);

		return {
			...assignment,
			contacts,
			contactsCount: assignment.contactsCount ?? contacts.length,
			additionalAssignments,
		};
	}
}
